// ============================================
// LIGA AJUPAM - Teams Service
// ============================================
// CRUD operations for teams (parejas)
// @ts-nocheck - Supabase types use simplified `any` for flexibility

import { supabase, executeQuery, handleSupabaseError } from './client';
import { PROFILE_PUBLIC_COLUMNS, fetchAdminPrivateProfiles, teamPlayerIds, teamWithPrivateFields } from './profilePrivacy';
import type { Team, TeamWithDetails, TournamentCategory } from '../../types';
import type { TeamAvailability, TeamCompatibility, TeamFixedSlot, CompatLevel } from '../../utils/availability';
import type { PendingRival, PlayerRivalContextResponse } from '../../utils/challengeRivalContext';
import { validateTeamCategories } from './categories';
import { getCurrentSession } from './auth';
import { hasObscenity } from '../../utils/nameNormalization';

// Helper para llamar RPC functions con tipado flexible
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const callRpc = async (fnName: string, params: Record<string, any>): Promise<any> => {
  const { data, error } = await (supabase.rpc as any)(fnName, params);
  if (error) throw handleSupabaseError(error);
  return data;
};

/**
 * Validar nombre de equipo en frontend (pre-validación SÍNCRONA)
 * v4.2.7: Valida groserías, longitud, caracteres especiales
 *
 * Esta validación se ejecuta ANTES de llamar al backend para dar
 * feedback inmediato al usuario.
 *
 * Validaciones:
 * 1. No vacío
 * 2. Longitud entre 3 y 50 caracteres
 * 3. Sin groserías (usando diccionario OBSCENITY)
 * 4. Solo letras, espacios, guiones y apóstrofes
 *
 * Para validación completa (incluyendo duplicados), usar validateTeamNameFrontend()
 */
export const validateTeamNameSync = (name: string): {
  isValid: boolean;
  error?: string;
} => {
  // 1. Validar no vacío
  const trimmed = name.trim();
  if (!trimmed) {
    return { isValid: false, error: 'El nombre del equipo no puede estar vacío' };
  }

  // 2. Validar longitud mínima
  if (trimmed.length < 3) {
    return { isValid: false, error: 'El nombre debe tener al menos 3 caracteres' };
  }

  // 3. Validar longitud máxima
  if (trimmed.length > 25) {
    return { isValid: false, error: 'El nombre no puede tener más de 25 caracteres' };
  }

  // 4. Validar groserías (usando función existente de nameNormalization.ts)
  if (hasObscenity(trimmed)) {
    return { isValid: false, error: 'El nombre contiene palabras inapropiadas' };
  }

  // 5. Validar caracteres válidos (letras, números, espacios, guiones, apóstrofes, comas y puntos)
  // Permitir: a-z, A-Z, 0-9, áéíóúÁÉÍÓÚñÑüÜ, espacios, guiones (-), apóstrofes ('), comas (,), puntos (.)
  // v6.11.16.05: se agregan números para habilitar nombres como "LOS PANAS 2.0".
  const validPattern = /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ\s'.,\-]+$/;
  if (!validPattern.test(trimmed)) {
    return {
      isValid: false,
      error: 'El nombre solo puede contener letras, números, espacios, guiones, apóstrofes, comas y puntos'
    };
  }

  return { isValid: true };
};

/**
 * Validar nombre de equipo en frontend (ASÍNCRONA - validación completa)
 * v4.5.2: Valida groserías, longitud, caracteres + duplicados/similitud
 *
 * @param name - Nombre del equipo a validar
 * @param excludeTeamId - ID del equipo a excluir de la validación (útil al editar)
 */
export const validateTeamNameFrontend = async (
  name: string,
  excludeTeamId?: string,
  leagueId?: string | null
): Promise<{
  isValid: boolean;
  error?: string;
}> => {
  // 1. Validación síncrona (formato, groserías, caracteres)
  const syncValidation = validateTeamNameSync(name);
  if (!syncValidation.isValid) {
    return syncValidation;
  }

  // 2. Validación asíncrona (duplicados/similitud). v6.11.12: scope por-liga.
  try {
    const isAvailable = await checkTeamNameAvailable(name, excludeTeamId, leagueId);
    if (!isAvailable) {
      return {
        isValid: false,
        error: 'Ya existe un equipo con este nombre o uno muy similar'
      };
    }

    return { isValid: true };
  } catch (error) {
    console.error('Error validating team name:', error);
    // En caso de error, permitir (graceful degradation)
    return { isValid: true };
  }
};

/**
 * Verificar si el nombre del equipo está disponible - v4.1.1 usando RPC
 * Usa normalización server-side y detecta nombres duplicados
 */
export const checkTeamNameAvailable = async (
  name: string,
  excludeTeamId?: string,
  leagueId?: string | null,
): Promise<boolean> => {
  try {
    const isAvailable = await callRpc('check_team_name_available', {
      p_team_name: name,
      p_exclude_team_id: excludeTeamId || null,
      // v6.11.12: si se pasa la liga, la unicidad se scopea a esa liga; si es
      // null, cae al comportamiento global previo (fallback seguro).
      p_league_id: leagueId || null,
    });

    return isAvailable;
  } catch (error: any) {
    console.error('Error checking team name availability:', error);
    // En caso de error, permitir (graceful degradation)
    return true;
  }
};

/**
 * Obtener equipos con nombre similar al buscado (para mostrar al admin antes de forzar cambio)
 */
export const getSimilarTeamNames = async (
  name: string,
  excludeTeamId?: string,
  leagueId?: string | null,
): Promise<Array<{ team_name: string; reason: string }>> => {
  try {
    const results = await callRpc('get_similar_team_names', {
      p_team_name: name,
      p_exclude_team_id: excludeTeamId || null,
      p_league_id: leagueId || null,  // v6.11.12: scope por-liga
    });
    return Array.isArray(results) ? results : [];
  } catch (error: any) {
    console.error('Error getting similar team names:', error);
    return [];
  }
};

/**
 * Crear nuevo equipo
 * Verifica cupos disponibles y asigna status ACTIVO o SUPLENTE
 * El invite_code se genera automáticamente por un trigger en la BD
 */
export const createTeam = async (data: {
  name: string;
  category_id: string;
  captain_id: string;
  league_id?: string | null;  // v6.11.12: scope por-liga del check de nombre
}): Promise<Team> => {
  try {
    const currentSession = getCurrentSession();
    if (!currentSession?.token) {
      throw new Error('No hay sesión activa');
    }

    // ✅ v4.2.7: Validación frontend de nombre (groserías, longitud, caracteres)
    const frontendValidation = await validateTeamNameFrontend(data.name, undefined, data.league_id);
    if (!frontendValidation.isValid) {
      throw new Error(frontendValidation.error);
    }

    // Verificar que el nombre del equipo no exista (usando validación inteligente)
    const isAvailable = await checkTeamNameAvailable(data.name, undefined, data.league_id);
    if (!isAvailable) {
      throw new Error('TEAM_NAME_EXISTS:Ya existe un equipo con un nombre similar. Por favor elige otro.');
    }

    // Llamar RPC para crear equipo (v4.2.7: con validación completa en backend)
    console.log('[DEBUG] Calling user_create_team with params:', {
      p_session_token: currentSession.token,
      p_name: data.name.trim(),
      p_category_id: data.category_id,
      p_captain_id: data.captain_id
    });

    const result = await callRpc('user_create_team', {
      p_session_token: currentSession.token,
      p_name: data.name.trim(),  // ← Trim antes de enviar
      p_category_id: data.category_id,
      p_captain_id: data.captain_id
    });

    return result as Team;
  } catch (error: any) {
    console.error('Error creating team:', error);
    throw error;
  }
};

/**
 * Unirse a un equipo usando código de invitación
 */
export const joinTeam = async (inviteCode: string, partnerId: string): Promise<Team> => {
  try {
    const currentSession = getCurrentSession();
    if (!currentSession?.token) {
      throw new Error('No hay sesión activa');
    }

    // Llamar RPC para unirse al equipo
    const result = await callRpc('user_join_team', {
      p_session_token: currentSession.token,
      p_invite_code: inviteCode,
      p_partner_id: partnerId
    });

    return result as Team;
  } catch (error: any) {
    console.error('Error joining team:', error);
    throw error;
  }
};

/**
 * Obtener equipo por ID con detalles completos
 */
export const getTeamById = async (teamId: string): Promise<TeamWithDetails | null> => {
  try {
    const { data, error } = await supabase
      .from('teams')
      .select(`
        *,
        captain:captain_id(${PROFILE_PUBLIC_COLUMNS}),
        partner:partner_id(${PROFILE_PUBLIC_COLUMNS}),
        category:category_id(*)
      `)
      .eq('id', teamId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw handleSupabaseError(error);
    }

    return data as TeamWithDetails;
  } catch (error: any) {
    console.error('Error getting team:', error);
    throw error;
  }
};

/** Jugadores que muestra cada equipo en las placas de fixture/resultados. */
export const PLAYERS_PER_TEAM = 2;

// Tope de IDs por request. El límite real es la longitud de la URL que acepta
// PostgREST: `.in('col', [...])` viaja como query string y con ~1500 IDs armaba
// URLs de 58 KB que el servidor rechazaba con un error de CORS engañoso (ver
// docs/lessons/POSTGREST_URL_LIMIT_IN_FILTER_v6_7_10.md, donde la regla que
// quedó fue "seguro hasta ~150"). Un día de fixture rara vez pasa de un lote.
export const TEAM_IDS_PER_REQUEST = 100;

/**
 * v6.11.75 — Jugadores de varios equipos, en una query por lote.
 *
 * Antes esto vivía inline en AdminShareGenerator, dentro de un `Promise.all`
 * sobre los partidos, y hacía DOS GET a `profiles` por partido — uno por
 * equipo. Sentry lo levantó como LIGA-AJUPAM-W (`N+1 API Call` en
 * /admin-liga): 6 requests seriados de ~120ms dentro de una navegación de
 * 469ms, y eso con sólo tres partidos. Un día completo de fixture son decenas,
 * y el patrón crece lineal con ellos.
 *
 * v6.11.221 — **la pareja sale de `teams`, no de `profiles.current_team_id`.**
 * Hasta acá se leía `profiles` por `current_team_id` y se tomaban los 2 primeros
 * por nombre. Eso asume "un equipo = 2 perfiles vinculados", y no es cierto: una
 * suplencia aprobada después del primer partido y no permanente deja vinculado
 * al jugador ORIGINAL (`admin_review_substitution` sólo lo libera antes del
 * debut o si es permanente), o sea que el equipo tiene 3 o 4 perfiles. Con el
 * orden alfabético, el recorte a 2 se quedaba con los originales y dejaba afuera
 * a los que juegan: 30 de 316 equipos de la liga vigente, 314 partidos jugados
 * y 54 por jugar. `teams.captain_id/partner_id` es el par vigente —lo actualiza
 * cada suplencia, y es lo que leen las tablas de posiciones, la ficha del equipo
 * y la placa de Partido—, así que las placas de fixture y resultados tienen que
 * leer de ahí para no contradecirse entre sí.
 *
 * El orden es el del rol (capitán, compañero): determinista sin ORDER BY, y es
 * el mismo que muestran las demás placas.
 *
 * Devuelve un Map por `team_id`; un equipo sin jugadores simplemente no está.
 */
export const getPlayersByTeamIds = async (
  teamIds: string[]
): Promise<Map<string, { full_name: string }[]>> => {
  const byTeam = new Map<string, { full_name: string }[]>();
  if (teamIds.length === 0) return byTeam;

  // PostgREST entrega el embed a-uno como objeto, pero según cómo se tipe el
  // cliente puede venir envuelto en un array: se acepta cualquiera de los dos.
  type PlayerEmbed = { full_name: string | null } | { full_name: string | null }[] | null;
  type TeamPairRow = { id: string; captain: PlayerEmbed; partner: PlayerEmbed };
  const one = (embed: PlayerEmbed): { full_name: string | null } | null =>
    (Array.isArray(embed) ? embed[0] : embed) ?? null;

  for (let i = 0; i < teamIds.length; i += TEAM_IDS_PER_REQUEST) {
    const chunk = teamIds.slice(i, i + TEAM_IDS_PER_REQUEST);
    const { data, error } = await supabase
      .from('teams')
      .select('id, captain:captain_id(full_name), partner:partner_id(full_name)')
      .in('id', chunk);

    if (error) throw handleSupabaseError(error);

    for (const row of (data || []) as unknown as TeamPairRow[]) {
      const players = [one(row.captain), one(row.partner)]
        .filter((p): p is { full_name: string } => !!p?.full_name)
        .map((p) => ({ full_name: p.full_name }))
        .slice(0, PLAYERS_PER_TEAM);
      if (players.length > 0) byTeam.set(row.id, players);
    }
  }

  return byTeam;
};

/**
 * v6.11.22 — Estado de lista de espera del equipo actual del jugador.
 * Devuelve null si el equipo no está en lista de espera.
 */
export interface MyWaitlistStatus {
  team_id: string;
  category_name: string;
  position: number;
  total: number;
  is_complete: boolean;
  payment_status: string;
}
export const getMyWaitlistStatus = async (): Promise<MyWaitlistStatus | null> => {
  try {
    const currentSession = await getCurrentSession();
    if (!currentSession?.token) return null;
    const result = await callRpc('user_get_waitlist_status', {
      p_session_token: currentSession.token,
    });
    const row = Array.isArray(result) ? result[0] : result;
    return row ?? null;
  } catch (error: any) {
    console.error('Error getting waitlist status:', error);
    return null;
  }
};

/**
 * Obtener todos los equipos de una categoría
 */
export const getTeamsByCategory = async (
  categoryId: string
): Promise<TeamWithDetails[]> => {
  try {
    const teams = await executeQuery<TeamWithDetails[]>(() =>
      supabase
        .from('teams')
        .select(`
          *,
          captain:captain_id(${PROFILE_PUBLIC_COLUMNS}),
          partner:partner_id(${PROFILE_PUBLIC_COLUMNS}),
          category:category_id(*)
        `)
        .eq('category_id', categoryId)
        .order('points', { ascending: false })
    );

    return teams || [];
  } catch (error: any) {
    console.error('Error getting teams by category:', error);
    throw error;
  }
};

/**
 * Obtener tabla de posiciones por categoría
 * Ordenado por: puntos, diferencia de sets, diferencia de games
 */
export const getStandingsByCategory = async (
  categoryId: string
): Promise<TeamWithDetails[]> => {
  try {
    const teams = await executeQuery<TeamWithDetails[]>(() =>
      supabase
        .from('teams')
        .select(`
          *,
          captain:captain_id(${PROFILE_PUBLIC_COLUMNS}),
          partner:partner_id(${PROFILE_PUBLIC_COLUMNS}),
          category:category_id(*)
        `)
        .eq('category_id', categoryId)
        .eq('status', 'ACTIVO')
        .order('points', { ascending: false })
    );

    if (!teams) return [];

    // Ordenar con criterios de desempate
    const sorted = teams.sort((a, b) => {
      // 1. Por puntos
      if (b.points !== a.points) return b.points - a.points;

      // 2. Por diferencia de sets
      const diffSetsA = a.sets_won - a.sets_lost;
      const diffSetsB = b.sets_won - b.sets_lost;
      if (diffSetsB !== diffSetsA) return diffSetsB - diffSetsA;

      // 3. Por diferencia de games
      const diffGamesA = a.games_won - a.games_lost;
      const diffGamesB = b.games_won - b.games_lost;
      return diffGamesB - diffGamesA;
    });

    return sorted;
  } catch (error: any) {
    console.error('Error getting standings:', error);
    throw error;
  }
};

/**
 * Actualizar nombre de equipo
 * v4.2.0: Migrado a RPC user_update_team_name con session_token
 */
export const updateTeamName = async (
  teamId: string,
  newName: string,
  leagueId?: string | null,  // v6.11.12: scope por-liga del check de nombre
): Promise<Team> => {
  try {
    const currentSession = getCurrentSession();
    if (!currentSession?.token) {
      throw new Error('No hay sesión activa');
    }

    // ✅ v4.2.7: Validación frontend de nombre (groserías, longitud, caracteres)
    const frontendValidation = await validateTeamNameFrontend(newName, teamId, leagueId);
    if (!frontendValidation.isValid) {
      throw new Error(frontendValidation.error);
    }

    // v4.2.0: Llamar RPC con session_token (ya no necesita teamId)
    // v4.2.7: Trim del nombre antes de enviar
    const team = await callRpc('user_update_team_name', {
      p_session_token: currentSession.token,
      p_new_name: newName.trim(),
    });

    return Array.isArray(team) ? team[0] : team;
  } catch (error: any) {
    console.error('Error updating team name:', error);
    throw error;
  }
};

/**
 * Eliminar equipo (cambiar status a ELIMINADO)
 * Libera el cupo en la categoría si el equipo era ACTIVO
 */
export const deleteTeam = async (teamId: string): Promise<void> => {
  try {
    const currentSession = getCurrentSession();
    if (!currentSession?.token) {
      throw new Error('No hay sesión activa');
    }

    // Llamar RPC para eliminar equipo
    await callRpc('user_delete_team', {
      p_session_token: currentSession.token,
      p_team_id: teamId
    });
  } catch (error: any) {
    console.error('Error deleting team:', error);
    throw error;
  }
};

/**
 * Obtener equipos bloqueados (para admin)
 */
export const getBlockedTeams = async (): Promise<TeamWithDetails[]> => {
  try {
    const teams = await executeQuery<TeamWithDetails[]>(() =>
      supabase
        .from('teams')
        .select(`
          *,
          captain:captain_id(${PROFILE_PUBLIC_COLUMNS}),
          partner:partner_id(${PROFILE_PUBLIC_COLUMNS}),
          category:category_id(*)
        `)
        .eq('is_blocked', true)
    );

    return teams || [];
  } catch (error: any) {
    console.error('Error getting blocked teams:', error);
    throw error;
  }
};

/**
 * Bloquear/Desbloquear equipo (admin)
 */
export const toggleTeamBlock = async (
  teamId: string,
  isBlocked: boolean
): Promise<Team> => {
  try {
    const team = await executeQuery<Team>(() =>
      supabase
        .from('teams')
        .update({ is_blocked: isBlocked })
        .eq('id', teamId)
        .select()
        .single()
    );

    return team;
  } catch (error: any) {
    console.error('Error toggling team block:', error);
    throw error;
  }
};

/**
 * Cambiar capitán de un equipo (intercambiar roles con el compañero)
 * v4.2.0: Migrado a RPC user_swap_captain_partner con session_token
 */
export const swapCaptainPartner = async (teamId: string): Promise<void> => {
  try {
    const currentSession = getCurrentSession();
    if (!currentSession?.token) {
      throw new Error('No hay sesión activa');
    }

    // v4.2.0: Llamar RPC con session_token (ya no necesita teamId)
    await callRpc('user_swap_captain_partner', {
      p_session_token: currentSession.token,
    });

  } catch (error: any) {
    console.error('Error swapping captain:', error);
    throw error;
  }
};

/**
 * Obtener link de WhatsApp de una zona
 */
export const getZoneWhatsapp = async (
  categoryId: string,
  zoneGroup: string
): Promise<string | null> => {
  try {
    const { data, error } = await supabase.rpc('get_zone_whatsapp_link', {
      p_category_id: categoryId,
      p_zone_group: zoneGroup
    });

    if (error) throw error;
    return data || null;
  } catch (error: any) {
    console.error('Error getting zone whatsapp:', error);
    return null;
  }
};

/**
 * v6.11.45 — Estado completo del CTA del grupo de zona para el participante
 * logueado: link, si el admin lo marcó como destacado, y si esta persona ya
 * declaró estar en el grupo.
 *
 * La categoría y la zona las deriva el RPC de la sesión, así que acá no se pasa
 * (ni se confía en) nada del cliente. Reemplaza a `getZoneWhatsapp` para la PWA;
 * aquella queda para el panel de admin, que sí consulta zonas ajenas.
 */
export interface ZoneWhatsappInfo {
  link: string | null;
  featured: boolean;
  joined: boolean;
  zone_group: string | null;
}

export const getZoneWhatsappInfo = async (
  sessionToken: string
): Promise<ZoneWhatsappInfo | null> => {
  try {
    const { data, error } = await (supabase.rpc as any)('user_get_zone_whatsapp_info', {
      p_session_token: sessionToken,
    });
    if (error) throw error;
    return (data as ZoneWhatsappInfo) ?? null;
  } catch (error: any) {
    console.error('Error getting zone whatsapp info:', error);
    return null;
  }
};

/**
 * Registra que el participante tocó el CTA (`CTA_CLICK`, intención) o que
 * confirmó estar en el grupo (`SELF_REPORTED`). Nunca lanza: es información
 * secundaria y no puede trabar la apertura del link.
 */
export const markZoneWhatsappJoined = async (
  sessionToken: string,
  source: 'CTA_CLICK' | 'SELF_REPORTED'
): Promise<void> => {
  try {
    const { error } = await (supabase.rpc as any)('user_mark_zone_whatsapp_joined', {
      p_session_token: sessionToken,
      p_source: source,
    });
    if (error) throw error;
  } catch (error: any) {
    console.error('Error marking zone whatsapp joined:', error);
  }
};

// ============================================
// ZONE-BASED FUNCTIONS (Módulo v2.0-3)
// ============================================

/**
 * Obtener zonas disponibles para una categoría
 */
export const getZonesByCategory = async (
  categoryId: string,
  leagueId?: string | null
): Promise<string[]> => {
  try {
    let query = supabase
      .from('teams')
      .select('zone_group')
      .eq('category_id', categoryId)
      .eq('status', 'ACTIVO')
      .not('zone_group', 'is', null);

    // v6.7.9: filtrar zonas por liga (defensa contra category_id reusado entre ligas)
    if (leagueId) {
      query = query.eq('league_id', leagueId);
    }

    const { data, error } = await query;

    if (error) throw handleSupabaseError(error);
    if (!data) return [];

    const uniqueZones = [...new Set(data.map(t => t.zone_group).filter(Boolean))] as string[];
    return uniqueZones.sort();
  } catch (error: any) {
    console.error('Error getting zones:', error);
    return [];
  }
};

/**
 * Obtener tabla de posiciones por categoría y zona
 * Utiliza la función RPC get_extended_standings para obtener el orden oficial
 */
export const getStandingsByCategoryAndZone = async (
  categoryId: string,
  zoneGroup?: string | null,
  leagueId?: string | null
): Promise<TeamWithDetails[]> => {
  try {
    const { data, error } = await supabase.rpc('get_extended_standings', {
      p_category_id: categoryId,
      p_zone_group: zoneGroup || 'Sin Zona',
      p_league_id: leagueId ?? null,
    } as any);

    if (error) throw handleSupabaseError(error);
    if (!data) return [];

    // Mapear el resultado plano a la estructura TeamWithDetails que espera el frontend
    return data.map((row: any) => ({
      ...row,
      id: row.team_id,
      name: row.team_name,
      captain: { id: row.captain_id, full_name: row.captain_name },
      partner: row.partner_id ? { id: row.partner_id, full_name: row.partner_name } : null,
      // Los otros campos (points, matches_played, average, etc) ya vienen en el row
    })) as unknown as TeamWithDetails[];
  } catch (error: any) {
    console.error('Error getting standings by zone:', error);
    throw error;
  }
};

/**
 * v6.11.140 — Contra qué compara la columna de movimiento de una zona: la foto
 * anterior a los ÚLTIMOS PARTIDOS (no una fecha del calendario), más la marca de
 * qué equipos se movieron en esa tanda.
 *
 * Va por separado de `getStandingsByCategoryAndZone` porque son dos preguntas
 * distintas: aquélla calcula la tabla, ésta lee historial. Y por eso **nunca
 * lanza**: si el historial falla, la tabla se dibuja igual con el criterio de
 * v6.11.138 (ver `utils/standingsMovement`). Un error acá no puede dejar al
 * jugador sin tabla de posiciones.
 */
export interface ZonePositionReference {
  team_id: string;
  /** Puesto en la foto anterior a los últimos partidos. Null si todavía no hay. */
  reference_position: number | null;
  /** `YYYY-MM-DD` — de qué día es esa foto. */
  reference_taken_on: string | null;
  /** v6.11.140 — Si este equipo se movió en la última tanda de partidos. */
  moved_in_last_batch: boolean;
  /** `YYYY-MM-DD` — el día (AR) de esa tanda. Igual para toda la zona. */
  movement_since: string | null;
}

export const getZonePositionReference = async (
  categoryId: string,
  zoneGroup?: string | null
): Promise<ZonePositionReference[]> => {
  try {
    const { data, error } = await supabase.rpc('get_zone_position_reference', {
      p_category_id: categoryId,
      p_zone_group: zoneGroup ?? null,
    });
    if (error) throw error;
    return (data as ZonePositionReference[]) || [];
  } catch (error) {
    console.error('Error getting zone position reference:', error);
    return [];
  }
};

// ============================================
// ALIASES FOR BACKWARDS COMPATIBILITY
// ============================================

/**
 * Alias para joinTeam (usado en Registration)
 */
export const joinTeamByCode = joinTeam;

/**
 * Alias para getStandingsByCategory (usado en Standings)
 */
export const getStandings = getStandingsByCategory;

/**
 * v6.11.213 — "Unirme con código": el equipo, su categoría y el capitán, por RPC.
 * Del capitán viaja el DNI (el chequeo de ranking de la pareja lo usa) pero no el
 * teléfono ni la fecha de nacimiento. Antes se leía `teams` con
 * `captain:captain_id(*)`, que deja de andar cuando se ocultan esas columnas.
 * Devuelve null si el código no existe.
 */
export const getTeamByInviteCode = async (inviteCode: string): Promise<TeamWithDetails | null> => {
  const { data, error } = await supabase.rpc('get_team_by_invite_code', { p_invite_code: inviteCode });
  if (error) throw handleSupabaseError(error);
  return (data as TeamWithDetails | null) ?? null;
};

/**
 * Obtener todos los equipos de una liga (para admin, v6.5.0).
 * Si `leagueId` es null/undefined, no se aplica filtro — legacy single-league.
 */
export const getAllTeams = async (
  leagueId?: string | null,
  options: { withPrivate?: boolean } = {}
): Promise<TeamWithDetails[]> => {
  try {
    let query = supabase
      .from('teams')
      .select(`
        *,
        captain:captain_id(${PROFILE_PUBLIC_COLUMNS}),
        partner:partner_id(${PROFILE_PUBLIC_COLUMNS}),
        category:category_id(*)
      `);
    if (leagueId) query = query.eq('league_id', leagueId);

    const { data: teams, error } = await query.order('registration_date', { ascending: false });

    if (error) throw handleSupabaseError(error);

    // v6.11.213: DNI, teléfono y nacimiento de los jugadores los da el servidor
    // al admin (sólo de sus ligas). Lo piden las pantallas que los muestran
    // (Equipos, Reemplazos); el resto no paga la llamada extra.
    if (options.withPrivate && teams?.length) {
      const privateMap = await fetchAdminPrivateProfiles(teamPlayerIds(teams));
      return teams.map((t) => teamWithPrivateFields(t, privateMap));
    }

    return teams || [];
  } catch (error: any) {
    console.error('Error fetching all teams:', error);
    throw error;
  }
};

// ─────────────────────────────────────────────────────────────
// v6.3.0: Actividad de equipos
// ─────────────────────────────────────────────────────────────

export interface TeamActivityEntry {
  team_id: string;
  team_name: string;
  category_name: string;
  category_branch: string;
  zone_group: string;
  proposals_sent: number;
  proposals_sent_confirmed: number;
  proposals_sent_expired: number;
  proposals_sent_rejected: number;
  proposals_confirmation_rate: number;
  proposals_received: number;
  proposals_received_accepted: number;
  proposals_received_rejected: number;
  proposals_acceptance_rate: number;
  matches_played: number;
  typical_days: Array<{ day: string; count: number }>;
  typical_hours: Array<{ range: string; count: number }>;
  typical_venues: Array<{ venue: string; count: number }>;
}

export interface TeamActivitySummary {
  typical_days: Array<{ day: string; count: number }>;
  typical_hours: Array<{ range: string; count: number }>;
  typical_venues: Array<{ venue: string; count: number }>;
}

export const getTeamActivityStats = async (
  adminSessionToken: string,
  leagueId?: string | null
): Promise<TeamActivityEntry[]> => {
  try {
    const { data, error } = await supabase.rpc('admin_get_team_activity', {
      p_admin_session_token: adminSessionToken,
      p_league_id: leagueId ?? null,
    } as any);
    if (error) throw handleSupabaseError(error);
    return (data || []) as TeamActivityEntry[];
  } catch (error: any) {
    console.error('Error fetching team activity stats:', error);
    throw error;
  }
};

export const getTeamActivitySummary = async (teamId: string): Promise<TeamActivitySummary | null> => {
  try {
    const { data, error } = await supabase.rpc('get_team_activity_summary', {
      p_team_id: teamId,
    });
    if (error) throw handleSupabaseError(error);
    const row = (data as any[])?.[0];
    if (!row) return null;
    return {
      typical_days:   row.typical_days   ?? [],
      typical_hours:  row.typical_hours  ?? [],
      typical_venues: row.typical_venues ?? [],
    } as TeamActivitySummary;
  } catch (error: any) {
    console.error('Error fetching team activity summary:', error);
    return null;
  }
};

// =============================================================================
// Disponibilidad declarada + Turnos fijos + Compatibilidad (v6.11.1)
// =============================================================================

export interface CompatibilityMapEntry {
  opponent_team_id: string;
  score: number | null;
  level: CompatLevel | null;
  already_played: boolean;
  both_declared: boolean;
}

// Lista de departamentos habilitados de una liga (para el multiselect).
export const getLeagueDepartmentsPublic = async (leagueId: string): Promise<string[]> => {
  try {
    const { data, error } = await supabase.rpc('get_league_departments_public', { p_league_id: leagueId });
    if (error) throw handleSupabaseError(error);
    return (data as any[] | null)?.map((r) => r.department) ?? [];
  } catch (error: any) {
    console.error('Error fetching league departments:', error);
    return [];
  }
};

// Disponibilidad declarada + turnos fijos de un equipo (público, para el modal).
export const getTeamAvailability = async (teamId: string): Promise<TeamAvailability | null> => {
  try {
    const { data, error } = await supabase.rpc('get_team_availability', { p_team_id: teamId });
    if (error) throw handleSupabaseError(error);
    const row = (data as any[])?.[0];
    if (!row) return null;
    return {
      preferred_days: row.preferred_days ?? [],
      preferred_time_blocks: row.preferred_time_blocks ?? [],
      preferred_departments: row.preferred_departments ?? [],
      updated_at: row.updated_at ?? null,
      fixed_slots: (row.fixed_slots ?? []) as TeamFixedSlot[],
    };
  } catch (error: any) {
    console.error('Error fetching team availability:', error);
    return null;
  }
};

// Guardar disponibilidad (capitán o compañero). Devuelve true/throw.
// p_team_id scopea la escritura al equipo que se está viendo: un usuario puede
// integrar varios equipos (uno por liga) y sin esto el guardado impactaba en
// otro equipo arbitrario.
export const setTeamAvailability = async (
  sessionToken: string,
  teamId: string,
  days: number[],
  timeBlocks: string[],
  departments: string[]
): Promise<void> => {
  const { error } = await supabase.rpc('user_set_team_availability', {
    p_session_token: sessionToken,
    p_team_id: teamId,
    p_days: days,
    p_time_blocks: timeBlocks,
    p_departments: departments,
  });
  if (error) throw handleSupabaseError(error);
};

export const setTeamFixedSlots = async (
  sessionToken: string,
  teamId: string,
  slots: TeamFixedSlot[]
): Promise<void> => {
  const { error } = await supabase.rpc('user_set_team_fixed_slots', {
    p_session_token: sessionToken,
    p_team_id: teamId,
    p_slots: slots,
  });
  if (error) throw handleSupabaseError(error);
};

// Mapa de compatibilidad del equipo del usuario vs su categoría/zona.
export const getTeamCompatibilityMap = async (
  sessionToken: string
): Promise<Map<string, CompatibilityMapEntry>> => {
  try {
    const { data, error } = await supabase.rpc('get_team_compatibility_map', { p_session_token: sessionToken });
    if (error) throw handleSupabaseError(error);
    const map = new Map<string, CompatibilityMapEntry>();
    for (const r of (data as any[] | null) ?? []) {
      map.set(r.opponent_team_id, {
        opponent_team_id: r.opponent_team_id,
        score: r.score,
        level: r.level,
        already_played: r.already_played,
        both_declared: r.both_declared,
      });
    }
    return map;
  } catch (error: any) {
    console.error('Error fetching compatibility map:', error);
    return new Map();
  }
};

// Compatibilidad de a pares (para el modal del equipo).
export const getTeamCompatibility = async (
  sessionToken: string,
  otherTeamId: string
): Promise<TeamCompatibility | null> => {
  try {
    const { data, error } = await supabase.rpc('get_team_compatibility', {
      p_session_token: sessionToken,
      p_other_team_id: otherTeamId,
    });
    if (error) throw handleSupabaseError(error);
    const row = (data as any[])?.[0];
    if (!row) return null;
    return {
      score: row.score,
      level: row.level,
      already_played: row.already_played,
      both_declared: row.both_declared,
      same_zone: row.same_zone,
    };
  } catch (error: any) {
    console.error('Error fetching team compatibility:', error);
    return null;
  }
};

// =============================================================================
// Standby (v6.11.2)
// =============================================================================

export interface TeamStandbyStatus {
  enabled: boolean;
  max_per_team: number;
  duration_days: number;
  used: number;
  remaining: number;
  is_on_standby: boolean;
  current_until: string | null;
  has_future: boolean;
  blackout_active: boolean;
  can_request: boolean;
}

export const getTeamStandbyStatus = async (teamId: string): Promise<TeamStandbyStatus | null> => {
  try {
    const { data, error } = await supabase.rpc('get_team_standby_status', { p_team_id: teamId });
    if (error) throw handleSupabaseError(error);
    const row = (data as any[])?.[0];
    return row ? (row as TeamStandbyStatus) : null;
  } catch (error: any) {
    console.error('Error fetching standby status:', error);
    return null;
  }
};

export const requestStandby = async (
  sessionToken: string,
  weeks: number,
  reason: string
): Promise<any> => {
  const { data, error } = await supabase.rpc('user_request_standby', {
    p_session_token: sessionToken,
    p_weeks: weeks,
    p_reason: reason,
  });
  if (error) throw handleSupabaseError(error);
  return data;
};

export const cancelStandby = async (sessionToken: string, standbyId?: string | null): Promise<any> => {
  const { data, error } = await supabase.rpc('user_cancel_standby', {
    p_session_token: sessionToken,
    p_standby_id: standbyId ?? null,
  });
  if (error) throw handleSupabaseError(error);
  return data;
};

// team_id -> fecha "until" (fin del standby vigente) de los equipos de mi liga en standby hoy
export const getLeagueStandbyTeamIds = async (sessionToken: string): Promise<Map<string, string>> => {
  try {
    const { data, error } = await supabase.rpc('get_league_standby_team_ids', { p_session_token: sessionToken });
    if (error) throw handleSupabaseError(error);
    const map = new Map<string, string>();
    for (const r of (data as any[] | null) ?? []) map.set(r.team_id, r.until);
    return map;
  } catch (error: any) {
    console.error('Error fetching standby team ids:', error);
    return new Map();
  }
};

export const adminUpdateStandbyConfig = async (
  adminSessionToken: string,
  leagueId: string,
  cfg: { enabled: boolean; max: number; duration: number; blackout: number }
): Promise<void> => {
  const { error } = await supabase.rpc('admin_update_standby_config', {
    p_admin_session_token: adminSessionToken,
    p_league_id: leagueId,
    p_enabled: cfg.enabled,
    p_max: cfg.max,
    p_duration: cfg.duration,
    p_blackout: cfg.blackout,
  });
  if (error) throw handleSupabaseError(error);
};

// =============================================================================
// Vinculación con el Ranking unificado (2026-06)
// =============================================================================
// `enabled` (master ON/OFF) es campo protegido: el backend lo rechaza si el rol
// no es SUPER_ADMIN. `mode` ('block' | 'warning') lo edita el admin de liga.
export const adminUpdateRankingLinkConfig = async (
  adminSessionToken: string,
  leagueId: string,
  cfg: { enabled?: boolean; mode?: 'block' | 'warning' }
): Promise<void> => {
  const { error } = await supabase.rpc('admin_update_ranking_link_config', {
    p_admin_session_token: adminSessionToken,
    p_league_id: leagueId,
    p_enabled: cfg.enabled ?? null,
    p_mode: cfg.mode ?? null,
  });
  if (error) throw handleSupabaseError(error);
};

// =============================================================================
// Desafíos (v6.11.3)
// =============================================================================

export interface ChallengeListItem {
  challenge_id: string;
  /**
   * v6.11.158 — PENDING_ADMIN (esperando al organizador) / PENDING (el retado tiene
   * que fijar) / CONFIRMED. Los que esperan al organizador vienen sin countdown:
   * el reloj del retado arranca cuando se aprueba, no cuando se pide.
   */
  status?: string;
  match_id: string | null;
  reason: string | null;
  created_at: string;
  other_team_id: string;
  other_team_name: string;
  i_am_challenger?: boolean;
  match_date: string | null;
  match_time: string | null;
  venue_id: string | null;
  venue_name: string | null;
  // v6.11.8 — countdown al timeout para los PENDING (incoming/outgoing).
  // CONFIRMED no los expone (no aplican).
  deadline_at?: string | null;
  seconds_remaining?: number | null;
}

export interface TeamChallengeStatus {
  enabled: boolean;
  max_per_team: number;
  used: number;
  remaining: number;
  // v6.11.8 — config del nuevo flujo expuesta para que el FE pueda validar/avisar
  response_timeout_hours: number;
  reminder_hours_before: number;
  require_venue: boolean;
  incoming_pending: ChallengeListItem[];
  outgoing_pending: ChallengeListItem[];
  confirmed: ChallengeListItem[];
}

// v6.11.156 — La traducción vive en `utils/challengeErrors` (módulo puro, con
// banco propio: `npm run test:challenge-errors`). Se re-exporta desde acá porque
// todas las pantallas la importan de este archivo desde v6.11.3.
export { parseChallengeError } from '../../utils/challengeErrors';

export const getTeamChallengeStatus = async (teamId: string): Promise<TeamChallengeStatus | null> => {
  try {
    const { data, error } = await supabase.rpc('get_team_challenge_status', { p_team_id: teamId });
    if (error) throw handleSupabaseError(error);
    return (data as TeamChallengeStatus) ?? null;
  } catch (error: any) {
    console.error('Error fetching challenge status:', error);
    return null;
  }
};

// v6.11.156 — ¿Puede mi equipo desafiar a ESE rival? La contesta la misma función
// que valida al crear (`_challenge_propose_block`), así que el botón "Desafiar" no
// ofrece algo que después rebota. `reason_code` null = se puede; si no, lo traduce
// `parseChallengeError`.
export interface ChallengeEligibility {
  can_challenge: boolean;
  reason_code: string | null;
}

export const getChallengeEligibility = async (
  sessionToken: string,
  opponentTeamId: string
): Promise<ChallengeEligibility | null> => {
  try {
    const { data, error } = await supabase.rpc('get_challenge_eligibility', {
      p_session_token: sessionToken,
      p_opponent_team_id: opponentTeamId,
    });
    if (error) throw handleSupabaseError(error);
    const row = (data as any[])?.[0];
    if (!row) return null;
    return { can_challenge: !!row.can_challenge, reason_code: row.reason_code ?? null };
  } catch (error: any) {
    console.error('Error fetching challenge eligibility:', error);
    return null;
  }
};

// v6.11.230 — Antes de mandar el desafío: el puesto de mi pareja, el del rival y
// los rivales que todavía podría desafiar más cerca o arriba (sólo los libres,
// nunca por qué otro no lo está). Ante cualquier error devuelve null y el modal
// no muestra el aviso: nunca impide mandar el desafío.
export const getChallengeRivalContext = async (
  sessionToken: string,
  opponentTeamId: string
): Promise<PlayerRivalContextResponse | null> => {
  try {
    const { data, error } = await supabase.rpc('user_get_challenge_rival_context', {
      p_session_token: sessionToken,
      p_opponent_team_id: opponentTeamId,
    });
    if (error) throw handleSupabaseError(error);
    return (data as PlayerRivalContextResponse) ?? null;
  } catch (error: unknown) {
    console.error('Error fetching challenge rival context:', error);
    return null;
  }
};

// v6.11.158 — El desafío no nace apuntando al rival: nace apuntando al ORGANIZADOR.
// El retador manda rival + justificación OBLIGATORIA (mínimo 20 caracteres, lo valida
// también el backend), queda en PENDING_ADMIN y el retado no se entera hasta que el
// organizador lo apruebe. La justificación NO le llega al retado.
export const proposeChallenge = async (
  sessionToken: string,
  opponentTeamId: string,
  justification: string
): Promise<{ success: boolean; match_id: string | null; challenge_id: string | null; message: string }> => {
  const { data, error } = await supabase.rpc('user_propose_challenge', {
    p_session_token: sessionToken,
    p_opponent_team_id: opponentTeamId,
    p_justification: justification,
  });
  if (error) throw handleSupabaseError(error);
  const row = (data as any[])?.[0] ?? { success: false, message: 'Sin respuesta' };
  if (!row.success) throw new Error(row.message || 'CHALLENGE_FAILED');
  return row;
};

// v6.11.160 — Los partidos que YA tienen las dos parejas en la semana del
// desafío. Es un aviso, no un bloqueo: el retado tiene un plazo que termina en
// W.O., y cerrarle días puede dejarlo sin ninguno (el organizador pidió
// expresamente "dejar programar igual, pero advertir").
export interface ChallengeDayConflict {
  match_date: string;
  /** true = partido de mi equipo; false = del rival del desafío. */
  is_mine: boolean;
  opponent_name: string;
  scheduled_time: string | null;
  venue_name: string | null;
}

export const getChallengeDayConflicts = async (
  sessionToken: string,
  challengeId: string
): Promise<ChallengeDayConflict[]> => {
  try {
    const { data, error } = await supabase.rpc('get_challenge_day_conflicts', {
      p_session_token: sessionToken,
      p_challenge_id: challengeId,
    });
    if (error) throw handleSupabaseError(error);
    return (data as ChallengeDayConflict[]) ?? [];
  } catch (error) {
    // El aviso es un extra: si falla, el modal sigue sirviendo para fijar.
    console.error('Error fetching challenge day conflicts:', error);
    return [];
  }
};

// v6.11.8 — El RETADO fija día, hora y cancha. Crea match en estado PROGRAMADO y
// pasa el challenge a CONFIRMED. La cancha es obligatoria si la liga lo exige
// (`require_venue` en TeamChallengeStatus).
export const fixChallengeSchedule = async (
  sessionToken: string,
  challengeId: string,
  matchDate: string,
  matchTime: string,
  venueId: string | null
): Promise<{ success: boolean; match_id: string | null; message: string }> => {
  const { data, error } = await supabase.rpc('user_fix_challenge_schedule', {
    p_session_token: sessionToken,
    p_challenge_id: challengeId,
    p_match_date: matchDate,
    p_match_time: matchTime,
    p_venue_id: venueId,
  });
  if (error) throw handleSupabaseError(error);
  const row = (data as any[])?.[0] ?? { success: false, message: 'Sin respuesta' };
  if (!row.success) throw new Error(row.message || 'CHALLENGE_FIX_FAILED');
  return row;
};

export const respondChallenge = async (
  sessionToken: string,
  challengeId: string,
  accept: boolean
): Promise<{ success: boolean; message: string }> => {
  const { data, error } = await supabase.rpc('user_respond_challenge', {
    p_session_token: sessionToken,
    p_challenge_id: challengeId,
    p_accept: accept,
  });
  if (error) throw handleSupabaseError(error);
  const row = (data as any[])?.[0] ?? { success: false, message: 'Sin respuesta' };
  if (!row.success) throw new Error(row.message || 'CHALLENGE_FAILED');
  return row;
};

export const cancelChallenge = async (
  sessionToken: string,
  challengeId: string
): Promise<{ success: boolean; message: string }> => {
  const { data, error } = await supabase.rpc('user_cancel_challenge', {
    p_session_token: sessionToken,
    p_challenge_id: challengeId,
  });
  if (error) throw handleSupabaseError(error);
  const row = (data as any[])?.[0] ?? { success: false, message: 'Sin respuesta' };
  if (!row.success) throw new Error(row.message || 'CHALLENGE_FAILED');
  return row;
};

export const adminUpdateChallengeConfig = async (
  adminSessionToken: string,
  leagueId: string,
  cfg: {
    enabled: boolean;
    max: number;
    // v6.11.223 — obsoletos: el Desafío puntúa con la tabla común y el motor ya no
    // lee estos valores. La RPC los acepta en null (COALESCE) y conserva la columna.
    pointsWin20?: number;
    pointsWin21?: number;
    pointsLose02?: number;
    pointsLose12?: number;
    pointsWoWin?: number;
    pointsWoLose?: number;
    allowedDays: number[];
    hoursStart: number;
    hoursEnd: number;
    // v6.11.8 — nuevos campos opcionales del flujo retador-propone/retado-fija
    responseTimeoutHours?: number;
    reminderHoursBefore?: number;
    requireVenue?: boolean;
    // v6.11.157 — exigir haberle propuesto un partido antes de poder desafiar
    requiresPriorAttempt?: boolean;
  }
): Promise<void> => {
  const { error } = await supabase.rpc('admin_update_challenge_config', {
    p_admin_session_token: adminSessionToken,
    p_league_id: leagueId,
    p_enabled: cfg.enabled,
    p_max: cfg.max,
    p_points_win_2_0: cfg.pointsWin20 ?? null,
    p_points_win_2_1: cfg.pointsWin21 ?? null,
    p_points_lose_0_2: cfg.pointsLose02 ?? null,
    p_points_lose_1_2: cfg.pointsLose12 ?? null,
    p_points_wo_win: cfg.pointsWoWin ?? null,
    p_points_wo_lose: cfg.pointsWoLose ?? null,
    p_allowed_days: cfg.allowedDays,
    p_hours_start: cfg.hoursStart,
    p_hours_end: cfg.hoursEnd,
    p_response_timeout_hours: cfg.responseTimeoutHours ?? null,
    p_reminder_hours_before: cfg.reminderHoursBefore ?? null,
    p_require_venue: cfg.requireVenue ?? null,
    p_requires_prior_attempt: cfg.requiresPriorAttempt ?? null,
  });
  if (error) throw handleSupabaseError(error);
};

// =============================================================================
// Admin: listado, summary y acciones de desafíos (v6.11.8)
// =============================================================================

export interface AdminChallengeRow {
  challenge_id: string;
  status: string;
  reason: string | null;
  /** v6.11.158 — Por qué el retador pide el desafío. La lee SOLO el organizador. */
  justification: string | null;
  created_at: string;
  responded_at: string | null;
  expires_at: string | null;
  reminder_sent_at: string | null;
  seconds_remaining: number;
  match_id: string | null;
  challenger_team_id: string;
  challenger_team_name: string;
  challenged_team_id: string;
  challenged_team_name: string;
  category_id: string | null;
  category_name: string | null;
  zone_group: string | null;
  match_date: string | null;
  match_time: string | null;
  venue_id: string | null;
  venue_name: string | null;
  match_status: string | null;
  match_type: string | null;
  /**
   * v6.11.229 — Puesto de hoy en la tabla de la zona (`teams.current_position`).
   * Opcionales: sin la migración no vienen y la pantalla no muestra la tabla.
   */
  challenger_position?: number | null;
  challenged_position?: number | null;
  zone_team_count?: number | null;
  /**
   * v6.11.229 — Sólo en desafíos vivos (PENDING_ADMIN, PENDING, CONFIRMED):
   * rivales de la zona que el retador todavía no jugó (sin el retado). En los
   * cerrados viene `null`: sería una foto de hoy sobre una decisión vieja.
   */
  pending_rivals?: PendingRival[] | null;
}

export interface AdminChallengesSummary {
  /** v6.11.158 — Pedidos esperando que el organizador apruebe o rechace. */
  pending_admin_count: number;
  pending_count: number;
  about_to_expire_count: number;
  overdue_count: number;
  confirmed_next_week_count: number;
  last_30_days: {
    played: number;
    rejected: number;
    expired: number;
    withdrawn: number;
    /** v6.11.158 — El organizador no lo dejó pasar. Sin WO y sin consumir cupo. */
    rejected_by_admin: number;
    /** v6.11.156 — Se cerró solo porque la pareja coordinó el partido. Sin WO. */
    fulfilled: number;
    cancelled_by_challenger: number;
    cancelled_by_challenged: number;
  };
}

export const adminListChallenges = async (
  adminSessionToken: string,
  leagueId: string,
  filters: {
    status?: string[];
    categoryId?: string;
    zoneGroup?: string;
    from?: string;
    to?: string;
    limit?: number;
  } = {}
): Promise<AdminChallengeRow[]> => {
  const { data, error } = await supabase.rpc('admin_list_challenges', {
    p_admin_session_token: adminSessionToken,
    p_league_id: leagueId,
    p_status_filter: filters.status ?? null,
    p_category_id: filters.categoryId ?? null,
    p_zone_group: filters.zoneGroup ?? null,
    p_from: filters.from ?? null,
    p_to: filters.to ?? null,
    p_limit: filters.limit ?? 200,
  });
  if (error) throw handleSupabaseError(error);
  return (data as AdminChallengeRow[]) ?? [];
};

export const adminGetChallengesSummary = async (
  adminSessionToken: string,
  leagueId: string
): Promise<AdminChallengesSummary> => {
  const { data, error } = await supabase.rpc('admin_get_challenges_summary', {
    p_admin_session_token: adminSessionToken,
    p_league_id: leagueId,
  });
  if (error) throw handleSupabaseError(error);
  return data as AdminChallengesSummary;
};

// v6.11.158 — La decisión del organizador sobre un pedido de desafío.
// Aprobar: recién ahí se entera el retado y arranca su reloj. Rechazar: muere sin
// W.O., sin consumir cupo, y sólo se entera el retador (con el motivo).
export const adminApproveChallenge = async (
  adminSessionToken: string,
  challengeId: string
): Promise<{ success: boolean; message: string }> => {
  const { data, error } = await supabase.rpc('admin_approve_challenge', {
    p_admin_session_token: adminSessionToken,
    p_challenge_id: challengeId,
  });
  if (error) throw handleSupabaseError(error);
  const row = (data as { success: boolean; message: string }[] | null)?.[0]
    ?? { success: false, message: 'Sin respuesta' };
  if (!row.success) throw new Error(row.message || 'CHALLENGE_APPROVE_FAILED');
  return row;
};

export const adminRejectChallenge = async (
  adminSessionToken: string,
  challengeId: string,
  reason: string
): Promise<{ success: boolean; message: string }> => {
  const { data, error } = await supabase.rpc('admin_reject_challenge', {
    p_admin_session_token: adminSessionToken,
    p_challenge_id: challengeId,
    p_reason: reason,
  });
  if (error) throw handleSupabaseError(error);
  const row = (data as { success: boolean; message: string }[] | null)?.[0]
    ?? { success: false, message: 'Sin respuesta' };
  if (!row.success) throw new Error(row.message || 'CHALLENGE_REJECT_FAILED');
  return row;
};

export const adminForceExpireChallenge = async (
  adminSessionToken: string,
  challengeId: string
): Promise<{ success: boolean; message: string }> => {
  const { data, error } = await supabase.rpc('admin_force_expire_challenge', {
    p_admin_session_token: adminSessionToken,
    p_challenge_id: challengeId,
  });
  if (error) throw handleSupabaseError(error);
  const row = (data as any[])?.[0] ?? { success: false, message: 'Sin respuesta' };
  if (!row.success) throw new Error(row.message || 'CHALLENGE_FORCE_EXPIRE_FAILED');
  return row;
};

export const adminCancelChallenge = async (
  adminSessionToken: string,
  challengeId: string,
  reason: string
): Promise<{ success: boolean; message: string }> => {
  const { data, error } = await supabase.rpc('admin_cancel_challenge', {
    p_admin_session_token: adminSessionToken,
    p_challenge_id: challengeId,
    p_reason: reason,
  });
  if (error) throw handleSupabaseError(error);
  const row = (data as any[])?.[0] ?? { success: false, message: 'Sin respuesta' };
  if (!row.success) throw new Error(row.message || 'CHALLENGE_ADMIN_CANCEL_FAILED');
  return row;
};

/**
 * v6.11.219 — Reabre un desafío REJECTED/EXPIRED: anula el W.O. (borra el
 * partido placeholder, con copia en la auditoría) y el retado vuelve a tener el
 * plazo de la liga para fijar. El motivo les llega a los dos equipos.
 */
export const adminReopenChallenge = async (
  adminSessionToken: string,
  challengeId: string,
  reason: string
): Promise<{ success: boolean; message: string }> => {
  const { data, error } = await supabase.rpc('admin_reopen_challenge', {
    p_admin_session_token: adminSessionToken,
    p_challenge_id: challengeId,
    p_reason: reason,
  });
  if (error) throw handleSupabaseError(error);
  const row = (data as { success: boolean; message: string }[] | null)?.[0]
    ?? { success: false, message: 'Sin respuesta' };
  if (!row.success) throw new Error(row.message || 'CHALLENGE_REOPEN_FAILED');
  return row;
};
